"use client";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import type { InfoTopBannerType } from "@/features/top-banners/api/get-info-top-banner";
import { TopBannerInfo } from "@/features/top-banners/components/top-banner-info";
import { TopBannerMobileApp } from "@/features/top-banners/components/top-banner-mobile-app";
import { useGetInfoTopBanners } from "@/features/top-banners/hooks/api/use-get-info-top-banner";
import { useGetMobileAppTopBanner } from "@/features/top-banners/hooks/api/use-get-mobile-banner";
import { getActiveInfoBanners } from "@/features/top-banners/utils/get-active-info-banners";
import { getLocaleForPathname, getPathname } from "@/i18n/navigation";
import { useGetTopBanners } from "../hooks/api/use-get-top-banners";
import useTopBannersStore from "../store/top-banners-store";
import { TopBannersItem } from "./top-banners-item";

export const TopBanners = () => {
  const { data } = useGetTopBanners({});
  const { data: mobileBanner } = useGetMobileAppTopBanner();
  const { data: infoBanners } = useGetInfoTopBanners();
  const setTotalHeight = useTopBannersStore((state) => state.setTotalHeight);
  const itemRefs = useRef<HTMLDivElement[]>([]);
  const totalHeight = useTopBannersStore((state) => state.totalHeight);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const locale = getLocaleForPathname();
  const burnLpPathname = getPathname({
    href: { pathname: "/burn" },
    locale,
  });
  const currentPathname = usePathname();
  const infoRefs = useRef(new Map<string, HTMLDivElement>());
  const [activeInfoBanners, setActiveInfoBanners] = useState<
    InfoTopBannerType[]
  >([]);

  useEffect(() => {
    setActiveInfoBanners(
      getActiveInfoBanners(infoBanners?.data ?? [], Date.now()),
    );
  }, [infoBanners]);

  const burnLpPage = currentPathname.includes(burnLpPathname);

  const filteredBanners = data?.data?.filter((banner) => {
    if (!banner.disabled_paths || banner.disabled_paths.length === 0) {
      return true;
    }

    const pathnameWithoutLocale = currentPathname.startsWith(`/${locale}`)
      ? currentPathname.slice(locale.length + 1)
      : currentPathname;

    const isDisabled = banner.disabled_paths.some((disabledPath) => {
      const path = disabledPath.text.startsWith("/")
        ? disabledPath.text
        : `/${disabledPath.text}`;

      return pathnameWithoutLocale.startsWith(path);
    });

    return !isDisabled;
  });

  const updateTotalHeight = useCallback(() => {
    const refs = [...infoRefs.current.values(), ...itemRefs.current];
    const totalHeight = refs.filter(Boolean).reduce((sum, ref) => {
      const existingClosed = JSON.parse(sessionStorage.getItem("tb") || "[]");

      const uuid = ref.getAttribute("data-uuid");
      if (existingClosed.includes(uuid)) return sum;

      return sum + (ref?.getBoundingClientRect().height || 0);
    }, 0);

    setTotalHeight(totalHeight);
  }, [setTotalHeight]);

  useEffect(() => {
    if (burnLpPage) {
      setTotalHeight(0);
      return;
    }

    const hasAnyBanner =
      activeInfoBanners.length > 0 ||
      (filteredBanners && filteredBanners.length > 0) ||
      !!mobileBanner?.data;

    if (!hasAnyBanner) {
      setTotalHeight(0);
      return;
    }

    resizeObserverRef.current = new ResizeObserver(updateTotalHeight);

    for (const ref of itemRefs.current.filter(Boolean)) {
      resizeObserverRef.current?.observe(ref);
    }

    for (const ref of infoRefs.current.values()) {
      resizeObserverRef.current?.observe(ref);
    }

    updateTotalHeight();

    return () => {
      for (const ref of itemRefs.current.filter(Boolean)) {
        resizeObserverRef.current?.unobserve(ref);
      }
      resizeObserverRef.current?.disconnect();
    };
  }, [
    filteredBanners,
    mobileBanner,
    setTotalHeight,
    burnLpPage,
    updateTotalHeight,
    activeInfoBanners,
  ]);

  const handleItemClose = (index: number) => {
    if (itemRefs.current[index]) {
      resizeObserverRef.current?.unobserve(itemRefs.current[index]);
    }

    itemRefs.current = itemRefs.current.filter((_, i) => i !== index);

    for (const ref of itemRefs.current) {
      if (ref) {
        resizeObserverRef.current?.observe(ref);
      }
    }

    updateTotalHeight();
  };

  if (burnLpPage) {
    return null;
  }

  return (
    <>
      <div
        className="h-0 transition-[height]"
        style={{ height: `${totalHeight}px` }}
      />
      <div className="absolute top-0 z-20 w-full">
        {activeInfoBanners.map((banner) => {
          const uuid = `info-banner-${banner.documentId}`;

          return (
            <TopBannerInfo
              key={uuid}
              onClose={updateTotalHeight}
              ref={(el: HTMLDivElement | null) => {
                if (el) {
                  infoRefs.current.set(uuid, el);
                } else {
                  infoRefs.current.delete(uuid);
                }
              }}
              text={banner.text}
              uuid={uuid}
            />
          );
        })}
        {filteredBanners?.map((banner, index) => (
          <TopBannersItem
            banner={banner}
            key={banner.uuid}
            onClose={() => handleItemClose(index)}
            ref={(el: HTMLDivElement | null) => {
              if (el) {
                if (index === 0) {
                  itemRefs.current = new Array(filteredBanners.length);
                }
                itemRefs.current[index] = el;

                if (index === filteredBanners.length - 1) {
                  updateTotalHeight();
                }
              }
            }}
          />
        ))}
        {mobileBanner?.data && (
          <TopBannerMobileApp
            banner={mobileBanner.data}
            onClose={() =>
              handleItemClose(filteredBanners ? filteredBanners.length : 0)
            }
            ref={(el: HTMLDivElement | null) => {
              if (el) {
                const index = filteredBanners ? filteredBanners.length : 0;
                itemRefs.current[index] = el;
                updateTotalHeight();
              }
            }}
          />
        )}
      </div>
    </>
  );
};
